import type { TFunction } from 'i18next'
import { Check, CircleStop, type LucideIcon, PhoneOff, Timer } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { WarningCallout } from '../controls/WarningCallout'
import { RecordingPlayer } from '../questioning/RecordingPlayer'
import { MONO, TEXT_CAPTION } from '../recipes'
import { formatSpeechClock } from './voice-format'
import {
  canKeepVoiceSample,
  isEnoughSpeech,
  VOICE_SAMPLE_MAX_ELAPSED_MS,
  VOICE_SAMPLE_MIN_SPEECH_MS,
  VOICE_SAMPLE_TARGET_SPEECH_MS,
} from './voice-rules'
import type { VoicePlayer, VoiceSampleContext, VoiceTakeSummary } from './voice-types'

export interface VoiceSampleReviewProps {
  take: VoiceTakeSummary
  context: Pick<VoiceSampleContext, 'mode' | 'sendsAtOnce'>
  /** The player on the recorded sample; none where the host plays nothing (a server render). */
  player?: VoicePlayer | null
}

const COPY = 'humint.voice.sample.review'

/** How a recording ended: `time` for one that ran its length with time counted, not speech. */
type EndingLook = VoiceTakeSummary['ending'] | 'time'

const ENDING: Record<EndingLook, { Icon: LucideIcon; tone: string }> = {
  target: { Icon: Check, tone: 'text-primary-strong' },
  stopped: { Icon: CircleStop, tone: 'text-foreground' },
  cap: { Icon: Timer, tone: 'text-foreground' },
  interrupted: { Icon: PhoneOff, tone: 'text-warning-foreground' },
  time: { Icon: Timer, tone: 'text-foreground' },
}

type Ended = Pick<VoiceTakeSummary, 'ending' | 'counting' | 'fileName'>

/** Thirty seconds of recording are no thirty seconds of speech: no target was reached. */
const endingLook = ({ ending, counting }: Ended): EndingLook =>
  ending === 'target' && counting === 'time' ? 'time' : ending

/** How a recording ended, in words: the review's first line, and what a screen reader is told. */
export function endingText(take: Ended, t: TFunction): string {
  if (take.fileName) return t('humint.voice.sample.file.selected')
  return t(`${COPY}.ending.${endingLook(take)}`, {
    limit: formatSpeechClock(VOICE_SAMPLE_MAX_ELAPSED_MS),
    target: formatSpeechClock(VOICE_SAMPLE_TARGET_SPEECH_MS),
  })
}

function EndingLine({ take }: { take: Ended }) {
  const { t } = useTranslation()
  const { Icon, tone } = ENDING[endingLook(take)]
  return (
    <p className={cn('flex items-center justify-center gap-2 text-sm font-semibold', tone)}>
      <Icon aria-hidden className="size-4 shrink-0" />
      {endingText(take, t)}
    </p>
  )
}

/**
 * A short recording cannot be kept. A low speech estimate asks for playback before server
 * validation; it cannot establish that the recording contains no speech.
 */
function warningText(take: VoiceTakeSummary, t: TFunction): string | null {
  if (take.fileName) return null
  const byTime = take.counting === 'time'
  if (!canKeepVoiceSample(take)) {
    return t(`${COPY}.tooLittleByTime`, {
      recorded: formatSpeechClock(take.durationMs),
      minimum: formatSpeechClock(VOICE_SAMPLE_MIN_SPEECH_MS),
    })
  }
  if (byTime) return t(`${COPY}.byTime`)
  return isEnoughSpeech(take.speechMs) ? null : t(`${COPY}.lowEstimate`)
}

/** When a collected sample reaches the server: with the subject, or the moment it is used. */
function CollectHint({ sendsAtOnce }: { sendsAtOnce: boolean }) {
  const { t } = useTranslation()
  return (
    <p className={cn(TEXT_CAPTION, 'text-center text-muted-foreground')}>
      {t(sendsAtOnce ? `${COPY}.sendsAtOnceHint` : `${COPY}.collectHint`)}
    </p>
  )
}

/**
 * Under the ring once a sample is recorded (ADR-0062): how the recording ended, the speech
 * counted in it against its whole length, and the sample to listen to. What stands in the way of
 * keeping it is said in place of the count — it holds the count that matters, the whole length
 * stands in the line over the ring — and before the player, so it is read before anything is
 * tapped.
 */
export function VoiceSampleReview({ take, context, player = null }: VoiceSampleReviewProps) {
  const { t } = useTranslation()
  const warning = warningText(take, t)
  return (
    <div className="flex w-full flex-col gap-3">
      <EndingLine take={take} />
      {warning ? (
        <WarningCallout>{warning}</WarningCallout>
      ) : take.fileName ? (
        <p className="text-center text-sm text-muted-foreground">
          {t('humint.voice.sample.file.checkHint')}
        </p>
      ) : (
        <p className={cn(MONO, 'text-center text-[13px] text-muted-foreground')}>
          {t(`${COPY}.counts`, {
            speech: formatSpeechClock(take.speechMs),
            total: formatSpeechClock(take.durationMs),
          })}
        </p>
      )}
      {player && (
        // The shared player names itself after a questioning: the group around it says what plays.
        <div role="group" aria-label={t(`${COPY}.player`)}>
          <RecordingPlayer
            audio={player.audio}
            playback={player.playback}
            waveform={take.waveform}
            actions={player.actions}
          />
        </div>
      )}
      {!warning && context.mode === 'collect' && (
        <CollectHint sendsAtOnce={context.sendsAtOnce ?? false} />
      )}
    </div>
  )
}
